import { TAROT_DECK } from '../data/tarotDeck';

// Generate card image list from TAROT_DECK data (single source of truth)
export const TAROT_CARDS = TAROT_DECK.map(card => card.imagePath);

// Card back images
export const CARD_BACKS = [
  '/assets/decorations/card-backs/rider-waite_back.webp',
];

// All images to preload (cards + backs)
export const ALL_CARD_IMAGES = [...TAROT_CARDS, ...CARD_BACKS];

export interface PreloadProgress {
  loaded: number;
  total: number;
  percentage: number;
  currentImage?: string;
}

export async function preloadCardImages(
  onProgress?: (progress: PreloadProgress) => void
): Promise<void> {
  const cacheKey = 'tarot-cards-preloaded-v2'; // Updated version for new paths
  
  // Check if already preloaded
  if (localStorage.getItem(cacheKey)) {
    console.log('Cards already preloaded');
    onProgress?.({
      loaded: ALL_CARD_IMAGES.length,
      total: ALL_CARD_IMAGES.length,
      percentage: 100
    });
    return;
  }

  const total = ALL_CARD_IMAGES.length;
  let loaded = 0;

  console.log(`Starting preload of ${total} card images...`);

  // Preload all images with progress tracking
  const promises = ALL_CARD_IMAGES.map((url, index) => {
    return new Promise<void>((resolve) => {
      const img = new Image();
      
      img.onload = () => {
        loaded++;
        const progress: PreloadProgress = {
          loaded,
          total,
          percentage: Math.round((loaded / total) * 100),
          currentImage: url
        };
        onProgress?.(progress);
        console.log(`Loaded ${loaded}/${total}: ${url}`);
        resolve();
      };
      
      img.onerror = () => {
        loaded++;
        const progress: PreloadProgress = {
          loaded,
          total,
          percentage: Math.round((loaded / total) * 100),
          currentImage: url
        };
        console.warn(`Failed to load: ${url}`);
        onProgress?.(progress);
        resolve(); // Don't fail entire preload for one image
      };
      
      // Start loading with a small delay to prevent overwhelming the browser
      setTimeout(() => {
        img.src = url;
      }, index * 10); // 10ms delay between each image
    });
  });

  await Promise.all(promises);
  
  // Mark as preloaded
  localStorage.setItem(cacheKey, 'true');
  localStorage.setItem('tarot-cards-preloaded-date', new Date().toISOString());
  
  console.log(`Successfully preloaded ${loaded}/${total} card images!`);
}

export function isPreloadComplete(): boolean {
  return localStorage.getItem('tarot-cards-preloaded-v2') === 'true';
}

export function clearPreloadCache(): void {
  localStorage.removeItem('tarot-cards-preloaded-v1'); // Clear old cache
  localStorage.removeItem('tarot-cards-preloaded-v2'); // Clear new cache
  localStorage.removeItem('tarot-cards-preloaded-date');
  console.log('Preload cache cleared');
}

export function getPreloadDate(): Date | null {
  const dateStr = localStorage.getItem('tarot-cards-preloaded-date');
  return dateStr ? new Date(dateStr) : null;
}